import '@resources/fontes/fontes.css'
import { WHATSAPP_NUMBER } from '@constants/contact'
import '@resources/css/donators.css'
import '@resources/css/patient.css'
import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { FiAlertTriangle } from 'react-icons/fi'
import { HiOutlineHandThumbUp } from 'react-icons/hi2'
import Header from '@components/Header/Header'
import Sidebar from '@components/Sidebar/Sidebar'
import LoadingSpinner from '@components/LoadingSpinner/LoadingSpinner'
import { GoPlusCircle } from 'react-icons/go'
import { BsChevronRight } from 'react-icons/bs'
import { Link, useNavigate } from 'react-router-dom'
import IDonor from '@interfaces/IDonor'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import * as $Order from '@services/Order'
import Pedidos from '@resources/imgs/Pedidos.svg'
import AuthContext from '@contexts/Auth'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { SelectionDonorCard } from '@components/Cards/DonorCard'
import { FaInfoCircle, FaWhatsapp } from 'react-icons/fa'
import { FaCircleQuestion } from 'react-icons/fa6'

const Patient: React.FC<any> = () => {
  const { onRemoveDonorFromCart, setCartCount, cart, activeInProgressOrders } = useContext(AuthContext)

  const [showVideoModal, setShowVideoModal] = useState(false)

  const handleHelpClick = () => setShowVideoModal(true)

  const handleCloseModal = () => setShowVideoModal(false)

  const [donors, setDonors] = useState<IDonor[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const navigate = useNavigate()

  const SweetAlert = useMemo(() => withReactContent(Swal), [])

  const fetchDonors = useCallback(async () => {
    setIsLoading(true)

    $Order.donors().then(({ data: cartDonors }) => {
      setDonors(cartDonors)
      setCartCount(cartDonors.length)
    }).catch((error) => {
      SweetAlert.fire('Erro', error.message || 'Erro ao buscar doadores do carrinho', 'error')
      navigate('/')
    }).finally(() => {
      setIsLoading(false)
    })
  }, [navigate, SweetAlert, setCartCount])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    fetchDonors()
  }, [cart, navigate, fetchDonors])

  useEffect(() => {
    const handleFocus = () => {
      fetchDonors()
    }

    window.addEventListener('focus', handleFocus)

    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [fetchDonors])

  useEffect(() => {
    const showSticky = activeInProgressOrders.length < 1 && donors.length > 0
    if (showSticky) {
      document.body.classList.add('has-sticky-cart-btn')
    } else {
      document.body.classList.remove('has-sticky-cart-btn')
    }
    return () => {
      document.body.classList.remove('has-sticky-cart-btn')
    }
  }, [activeInProgressOrders, donors])

  const handleRemoveFromCart = (id: string) => {
    onRemoveDonorFromCart(id).then(() => {
      setDonors((prevDonors) => prevDonors.filter((donor) => donor.internal_id !== id))
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setDonors((items) => {
        const oldIndex = items.findIndex((item) => item.internal_id === active.id)
        const newIndex = items.findIndex((item) => item.internal_id === over.id)

        const newOrder = arrayMove(items, oldIndex, newIndex)

        // Call API to update order
        const donorIds = newOrder.map((donor) => String(donor.internal_id))
        $Order.reorderDonors(donorIds).catch((error) => {
          SweetAlert.fire('Erro', error.message || 'Erro ao reordenar doadores', 'error')
          // Revert on error
          setDonors(items)
        })

        return newOrder
      })
    }
  }

  return (
    <>
      <div className="d-lg-flex justify-content-lg-center">
        <Sidebar />
        <main className="with-fixed-nav">
          <LoadingSpinner isLoading={isLoading} text="Carregando doadores..." />
          <Header showSidebarOpenButton />

          <section className="box-card py-3 mt-4" style={{ overflow: 'hidden', maxWidth: '100%' }}>
            <div className="row mb-4">
              <div className="col-auto pr-0">
                <Link to="/" className="text-body d-flex align-items-center">Catálogo</Link>
              </div>
              <div className="col-auto pl-1 pr-1">
                <BsChevronRight size={10} />
              </div>
              <div className="col-auto p-0">
                <a href="#" className="id d-flex align-items-center font-weight-bold">
                  Carrinho
                </a>
              </div>
            </div>

            <div className="baby d-mb-block d-flex justify-content-between align-items-center">
              <div className="text">
                {activeInProgressOrders.length < 1 ? (
                  <>
                    <h5>
                      Pronta para dar o próximo passo?

                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <FaCircleQuestion style={{ color: '#FA5A84', marginLeft: '5px', cursor: 'pointer', fontSize: '14px' }} onClick={handleHelpClick} />
                      </span>
                    </h5>
                    <p>Você pode iniciar uma <b>solicitação de pré-reserva</b> para os seus doadores escolhidos.</p>
                    <p>Basta ordenar os doadores de acordo com sua ordem de preferência.</p>
                    <p>A pré-reserva é válida por 2 dias. Dentro desse período, você deve realizar o pagamento para confirmar a reserva e garantir a amostra de sêmen em seu nome!</p>
                  </>
                ) : (
                  <h5>Solicitação de Reserva em Andamento</h5>
                )}
              </div>

              {activeInProgressOrders.length < 1 && (
                <Link
                  to={donors.length === 0 ? '#' : '/solicitacao'}
                  target="_blank"
                  className={`btn btn-primary d-flex align-items-center justify-content-around ${donors.length === 0 ? 'disabled' : ''}`}
                  style={{whiteSpace: 'nowrap'}}
                >
                  Fazer uma pré-reserva
                </Link>
              )}
            </div>

            {activeInProgressOrders.length >= 1 && (
              <div className="row my-3">
                <div className="col-12">
                  <div className="box payment p-4 mb-0 shadow-sm border-0" style={{
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #FA5A84 0%, #FF8BA7 100%)',
                    color: '#fff'
                  }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      <div className="text flex-grow-1 mb-3 mb-md-0">
                        <div className="d-flex align-items-center mb-2">
                          <div className="d-flex align-items-center justify-content-center" style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            marginRight: '14px'
                          }}
                          >
                            <FaInfoCircle size={20} className="text-white" />
                          </div>
                          <h5 className="mb-0 font-weight-bold text-white">Você possui uma solicitação de reserva em andamento</h5>
                        </div>
                        <p className="mb-0 text-white" style={{ marginLeft: '54px', opacity: 0.95, lineHeight: '1.5' }}>
                          Para visualizar o status, acesse a seção de Pedidos
                        </p>
                      </div>
                      <Link
                        to="/pedidos"
                        rel="noreferrer"
                        className="btn btn-light btn-lg px-5 py-3"
                        style={{
                          borderRadius: '10px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          textDecoration: 'none',
                          transition: 'all 0.2s',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          minWidth: '285px',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-center">
                          <img className="pr-2" src={Pedidos} alt="" style={{ color: '#FF8BA7' }} /> <span>Ver Pedidos</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>

                <div className="col-12 mt-3">
                  <div className="box payment p-4 mb-0 shadow-sm border-0" style={{
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgb(77, 97, 170) 0%, rgb(107, 127, 193) 100%)',
                    color: '#fff'
                  }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      <div className="text flex-grow-1 mb-3 mb-md-0">
                        <div className="d-flex align-items-center mb-2">
                          <div className="d-flex align-items-center justify-content-center" style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            marginRight: '14px'
                          }}
                          >
                            <FaInfoCircle size={20} className="text-white" />
                          </div>
                          <h5 className="mb-0 font-weight-bold text-white">Precisa fazer mais de um pedido?</h5>
                        </div>
                        <p className="mb-0 text-white" style={{ marginLeft: '54px', opacity: 0.95, lineHeight: '1.5' }}>
                          Entre em contato com o Pro-Seed pelo WhatsApp para realizar mais de um pedido
                        </p>
                      </div>
                      <a
                        target="_blank"
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Olá! Preciso fazer mais de um pedido.')}`}
                        rel="noreferrer"
                        className="btn btn-light btn-lg px-5 py-3"
                        style={{
                          borderRadius: '10px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          textDecoration: 'none',
                          transition: 'all 0.2s',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          minWidth: '285px'
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <FaWhatsapp size={20} className="mr-2" style={{ color: '#25D366' }} />
                          Falar no WhatsApp
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="row">
              <div className="col-md-12">
                <div className="w-100 alert alert-primary mt-3 d-none">
                  <div className="d-flex align-items-center">
                    <FiAlertTriangle />
                    <p className="mb-0 ml-3">Será necessário no mínimo a escolha de um doador e no máximo de cinco, fique a vontade para trocar.</p>
                  </div>
                </div>

                <div className={`w-100 alert alert-success mt-3 ${donors.length >= 1 ? 'd-block' : 'd-none'}`}>
                  <div className="d-flex align-items-center">
                    <HiOutlineHandThumbUp />
                    <p className="mb-0 ml-3">Sua reserva final será de apenas um desses doadores, baseada na confirmação de disponibilidade de amostra de sêmen, controle de gestações por doador na sua região e resultados do photomatching, se aplicável.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className={`row align-items-center ${donors.length === 0 ? 'd-none' : ''}`}>
              <div className="col-12">
                <div className="card-index mt-2">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={donors.map((d) => String(d.internal_id))}
                      strategy={verticalListSortingStrategy}
                    >
                      {donors.map((donor, i) => (
                        <SelectionDonorCard
                          key={donor.internal_id}
                          donor={donor}
                          position={i+1}
                          onRemove={handleRemoveFromCart}
                          isLast={i === donors.length - 1}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            </div>
          </section>

          {activeInProgressOrders.length < 1 && donors.length > 0 && (
            <div className="sticky-bottom-btn d-lg-none">
              <Link
                to="/solicitacao"
                target="_blank"
                className="btn btn-primary d-flex align-items-center justify-content-center"
              >
                Fazer uma pré-reserva
                <GoPlusCircle className="ml-2" />
              </Link>
            </div>
          )}
        </main>

      </div>

      {showVideoModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleCloseModal}>
          <div style={{ position: 'relative', width: 'min(1280px, 90vw)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} src="https://www.youtube.com/embed/mrdFnYAKM-0" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
            </div>
            <button style={{ position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }} onClick={handleCloseModal}>×</button>
          </div>
        </div>
      )}
    </>
  )
}

export default Patient
